import { create } from "zustand";
import { getCachedWorkers } from "./atelierStore";
import { disponibilitesApi } from "@services/api/disponibilites";
import {
  type AvailabilityBlock,
  type AvailabilityBlockType,
  type RecurringSummary,
  type CongeSummary,
  getMonday,
  getWeekDates,
} from "./disponibiliteStore";

/* ── Re-export shared types ── */

export type { AvailabilityBlock, AvailabilityBlockType, RecurringSummary };

/* ── Store ── */

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

interface ServiceRapideState {
  selectedDate: string;
  viewMode: "jour" | "semaine";
  selectedWorkerId: string;
  selectedWorkerIds: string[];
  blocks: AvailabilityBlock[];
  searchQuery: string;
  isLoading: boolean;
  _fetchedOrgId: string | undefined;

  fetchBlocks: (orgId: string) => Promise<void>;
  setSelectedDate: (date: string) => void;
  setViewMode: (mode: "jour" | "semaine") => void;
  setSelectedWorkerId: (id: string) => void;
  setSearchQuery: (query: string) => void;
  toggleWorkerSelection: (id: string) => void;
  selectAllWorkers: () => void;
  deselectAllWorkers: () => void;

  addBlock: (block: Omit<AvailabilityBlock, "id">, orgId: string) => void;
  updateBlock: (
    id: string,
    updates: Partial<Omit<AvailabilityBlock, "id">>,
  ) => void;
  removeBlock: (id: string) => void;

  getBlocksForWorkerWeek: (
    workerId: string,
    weekStart: string,
  ) => AvailabilityBlock[];
  getRecurringSummary: (workerId: string) => RecurringSummary[];
  getPunctualSummary: (workerId: string) => { label: string; totalHours: number }[];
}

export const useServiceRapideStore = create<ServiceRapideState>((set, get) => ({
  selectedDate: todayIso(),
  viewMode: "semaine",
  selectedWorkerId: "",
  selectedWorkerIds: [],
  blocks: [],
  searchQuery: "",
  isLoading: false,
  _fetchedOrgId: undefined,

  fetchBlocks: async (orgId: string) => {
    const state = get();
    if (state.isLoading || state._fetchedOrgId === orgId) return;
    set({ isLoading: true, _fetchedOrgId: orgId });
    try {
      const blocks = await disponibilitesApi.list({ org_id: orgId, category: "service_rapide" });
      const workers = getCachedWorkers();
      set({
        blocks,
        isLoading: false,
        selectedWorkerIds: workers.map((w) => w.id),
        selectedWorkerId: workers[0]?.id || "",
      });
    } catch (error) {
      console.error("Error fetching service rapide blocks:", error);
      set({ blocks: [], isLoading: false, _fetchedOrgId: undefined });
    }
  },

  setSelectedDate: (date) => set({ selectedDate: date }),
  setViewMode: (mode) => set({ viewMode: mode }),
  setSelectedWorkerId: (id) => set({ selectedWorkerId: id }),
  setSearchQuery: (query) => set({ searchQuery: query }),
  toggleWorkerSelection: (id) =>
    set((state) => ({
      selectedWorkerIds: state.selectedWorkerIds.includes(id)
        ? state.selectedWorkerIds.filter((wId) => wId !== id)
        : [...state.selectedWorkerIds, id],
    })),
  selectAllWorkers: () =>
    set({ selectedWorkerIds: getCachedWorkers().map((w) => w.id) }),
  deselectAllWorkers: () => set({ selectedWorkerIds: [] }),

  addBlock: (block, orgId) => {
    const tempId = `sr_${Date.now()}`;
    set((state) => ({
      blocks: [...state.blocks, { ...block, id: tempId }],
    }));
    const worker = getCachedWorkers().find((w) => w.id === block.workerId);
    disponibilitesApi.create(block, orgId, "service_rapide", worker?.name).then((created) => {
      set((state) => ({
        blocks: state.blocks.map((b) => (b.id === tempId ? created : b)),
      }));
    }).catch((e) => console.error("Error creating block:", e));
  },

  updateBlock: (id, updates) => {
    set((state) => ({
      blocks: state.blocks.map((b) => (b.id === id ? { ...b, ...updates } : b)),
    }));
    disponibilitesApi.update(id, updates).catch((e) => console.error("Error updating block:", e));
  },

  removeBlock: (id) => {
    set((state) => ({
      blocks: state.blocks.filter((b) => b.id !== id),
    }));
    disponibilitesApi.delete(id).catch((e) => console.error("Error deleting block:", e));
  },

  getBlocksForWorkerWeek: (workerId, weekStart) => {
    const { blocks, searchQuery } = get();
    const monday = getMonday(weekStart);
    const weekDates = getWeekDates(monday);

    return blocks.filter((b) => {
      if (b.workerId !== workerId) return false;
      if (
        searchQuery &&
        !b.label.toLowerCase().includes(searchQuery.toLowerCase())
      )
        return false;
      if (weekDates.includes(b.date)) return true;
      if (b.recurring && b.dayOfWeek !== undefined) return true;
      return false;
    });
  },

  getRecurringSummary: (workerId) => {
    const { blocks } = get();
    return blocks
      .filter((b) => b.workerId === workerId && b.recurring)
      .map((b) => ({ label: b.label, totalHours: b.durationHours }));
  },

  getPunctualSummary: (workerId) => {
    const { blocks } = get();
    return blocks
      .filter((b) => b.workerId === workerId && !b.recurring)
      .map((b) => ({ label: `${b.label} ${b.date}`, totalHours: b.durationHours }));
  },
}));
